import { Vector3 } from "three";
import { Arcs, ArcsOptions } from "../arcs/Arcs";

/**
 * Configuration for beaker arc spawning
 */
export interface BeakerArcsConfig {
  /** Size parameter matching morph-particles size (default: 800) */
  size: number;
  /** Maximum arcs at once (default: 4) */
  maxArcs: number;
  /** How often to spawn arcs [min, max] seconds (default: [0.3, 0.8]) */
  spawnInterval: [number, number];
  /** Arc lifetime [min, max] seconds (default: [0.2, 0.5]) */
  arcLifetime: [number, number];
}

const DEFAULT_BEAKER_CONFIG: BeakerArcsConfig = {
  size: 800,
  maxArcs: 6,
  spawnInterval: [0.2, 0.6],
  arcLifetime: [0.3, 0.6],
};

/**
 * Lightning arcs inside the beaker "liquid" area.
 * Arcs follow the slope of the beaker - straight down in center,
 * increasingly sloped toward the edges to match the beaker wall angle.
 */
export class BeakerArcs extends Arcs {
  private beakerConfig: BeakerArcsConfig;
  private lastSpawnTime = 0;
  private active = false;
  private transitionProgress = 0;

  // Pre-computed beaker dimensions
  private baseHeight: number;
  private baseRadius: number;
  private neckRadius: number;
  private liquidHeight: number;
  private yMin: number;

  constructor(
    beakerConfig: Partial<BeakerArcsConfig> = {},
    arcsOptions: ArcsOptions = {},
  ) {
    const config = { ...DEFAULT_BEAKER_CONFIG, ...beakerConfig };

    // Configure the base Arcs class for beaker lightning style
    super({
      ...arcsOptions,
      config: {
        animation: {
          horizontalEasing: 0.18,
          verticalEasing: 0.12,
          targetChangeChance: 0.1,
          horizontalRange: 0.4, // Less horizontal movement - more vertical
          verticalRange: 0.3,
          jaggedness: 25, // Less jagged for contained lightning
          segmentsMin: 15,
          segmentsRange: 8,
          curveSegments: 25,
        },
        spawn: {
          maxArcs: config.maxArcs,
          spawnInterval: config.spawnInterval,
          lifetime: config.arcLifetime,
          settlementDelay: 0,
          sineSpeed: 0,
        },
        style: {
          // Gold/yellow lightning for the liquid
          glowLayers: [
            [0.12, 0.15, false],
            [0.08, 0.25, false],
            [0.04, 0.4, false],
            [0.02, 0.6, false],
            [0.008, 1.0, true],
          ],
          glowLayersMobile: [
            [0.06, 0.3, false],
            [0.03, 0.55, false],
            [0.01, 1.0, true],
          ],
          glowColor: { r: 1.0, g: 0.85, b: 0.4 }, // Gold/yellow
          coreColor: { r: 1.0, g: 1.0, b: 0.9 }, // Warm white
        },
        ...arcsOptions.config,
      },
    });

    this.beakerConfig = config;

    // Calculate beaker dimensions (matching BeakerParticles.ts)
    const size = config.size;
    this.baseHeight = size * 1.0;
    this.baseRadius = size * 0.7;
    this.neckRadius = size * 0.2;
    this.liquidHeight = this.baseHeight * 0.85;
    this.yMin = -this.baseHeight / 2;
  }

  /**
   * Get the beaker radius at a given Y position
   */
  private getRadiusAtY(y: number): number {
    const t = (y - this.yMin) / this.baseHeight;
    return this.baseRadius * (1 - t) + this.neckRadius * t;
  }

  /**
   * Set the transition progress (0-1) for fading in/out with shape transitions
   */
  public setTransitionProgress(progress: number): void {
    this.transitionProgress = Math.max(0, Math.min(1, progress));
    this.setIntensity(this.transitionProgress);
    this.active = progress > 0.1;
  }

  /**
   * Check if arcs are currently active
   */
  public isActive(): boolean {
    return this.active;
  }

  /**
   * Update the beaker arcs
   */
  public updateBeaker(time: number, deltaTime: number = 1 / 60): void {
    if (!this.active) {
      this.clearArcs();
      return;
    }

    const { spawnInterval, arcLifetime, maxArcs } = this.beakerConfig;
    const [minInterval, maxInterval] = spawnInterval;

    // Try to spawn multiple arcs - each slot has independent spawn chance
    const currentArcCount = this.getArcCount();
    const availableSlots = maxArcs - currentArcCount;

    // For each available slot, calculate if we should spawn
    for (let slot = 0; slot < availableSlots; slot++) {
      // Base spawn chance per frame, scaled by available slots
      // This creates more natural random clustering
      const baseChance = deltaTime / ((minInterval + maxInterval) / 2);
      const spawnChance = baseChance * (0.5 + Math.random() * 0.5);

      if (Math.random() < spawnChance) {
        this.spawnBeakerArc(arcLifetime);
      }
    }

    // Update existing arcs
    this.update(deltaTime);
  }

  /**
   * Spawn a single beaker arc at a random location
   */
  private spawnBeakerArc(arcLifetime: [number, number]): void {
    // Arc starts below the top of liquid (85% of liquid height) to stay visually inside
    const topY = this.yMin + this.liquidHeight * 0.85;
    const bottomY = this.yMin + this.liquidHeight * 0.1; // Don't go all the way to bottom

    // Pick a random angle around the beaker
    const theta = Math.random() * Math.PI * 2;

    // Pick how far from center (0 = center, 1 = at wall)
    // Use sqrt for more even distribution across the area
    const radiusFraction = Math.sqrt(Math.random()) * 0.85; // Stay within liquid (90% of radius)

    // Get the radius at top and bottom of arc
    const topRadius = this.getRadiusAtY(topY) * 0.9; // 90% to stay in liquid area
    const bottomRadius = this.getRadiusAtY(bottomY) * 0.9;

    // Calculate horizontal positions
    // At center (radiusFraction=0), arc goes straight down
    // At edge (radiusFraction=1), arc follows the beaker slope
    const topR = topRadius * radiusFraction;
    const bottomR = bottomRadius * radiusFraction;

    // Start point (top of liquid)
    const startPoint = new Vector3(
      topR * Math.cos(theta),
      topY,
      topR * Math.sin(theta),
    );

    // End point (follows the slope - same angle, but radius scales with beaker shape)
    // Add small random variation to make it more natural
    const endTheta = theta + (Math.random() - 0.5) * 0.15; // Small angle variation
    const endPoint = new Vector3(
      bottomR * Math.cos(endTheta),
      bottomY + (Math.random() - 0.5) * this.liquidHeight * 0.2,
      bottomR * Math.sin(endTheta),
    );

    const [minLife, maxLife] = arcLifetime;
    this.spawnArc({
      startPoint,
      endPoint,
      lifetime: minLife + Math.random() * (maxLife - minLife),
      intensity: 0.7 + Math.random() * 0.3,
    });
  }

  public dispose(): void {
    super.dispose();
  }
}
